import { useEffect, useMemo, useCallback, useRef } from 'react';
import { Spin, Empty } from 'antd';
import { useEventSelection } from '../../../contexts/useEventSelection';
import { usePlayerOptional } from '../usePlayerContext';
import { useStationObservations } from '../useStationObservations';
import {
  AllStationsComparison,
  type ChartSettings,
} from '../../../components/AllStationsComparison/AllStationsComparison';
import type { WidgetProps } from './types';

export const AllStationsComparisonWidget = ({
  config,
  onConfigChange,
  openSettingsTrigger,
}: WidgetProps) => {
  const { selectedEvent } = useEventSelection();
  const player = usePlayerOptional();
  const { stations: allStations, loading, error } = useStationObservations();

  const selectedDateRange = player?.selectedDateRange ?? null;
  const chartSettings = (config.chartSettings as Partial<ChartSettings> | undefined) ?? undefined;

  const compactActionsRef = useRef<{
    openSettings: () => void;
    openFullScreen: () => void;
  } | null>(null);

  // Open the chart's settings drawer when the WidgetFrame gear is clicked
  useEffect(() => {
    if (openSettingsTrigger && openSettingsTrigger > 0) {
      compactActionsRef.current?.openSettings();
    }
  }, [openSettingsTrigger]);

  const handleSettingsChange = useCallback(
    (newSettings: ChartSettings) => {
      onConfigChange?.({ chartSettings: newSettings });
    },
    [onConfigChange],
  );

  const sessions = useMemo(
    () => selectedEvent?.sessions || [],
    [selectedEvent?.sessions],
  );

  const dateRange = useMemo(() => {
    if (!selectedDateRange) return undefined;
    return { start: selectedDateRange.start, end: selectedDateRange.end };
  }, [selectedDateRange]);

  if (loading && allStations.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>No event selected</span>}
        />
      </div>
    );
  }

  if (!selectedDateRange) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>Select a time range on the timeline</span>}
        />
      </div>
    );
  }

  if (error || allStations.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ fontSize: 11 }}>
              {error || 'No station data available'}
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', overflow: 'hidden' }}>
      <AllStationsComparison
        stations={allStations}
        circuitTimeZone={selectedEvent.circuit?.time_zone}
        sessions={sessions}
        variant="compact"
        embeddedInWidget
        hideTitle
        hideFullScreen
        disableAnimations
        compactActionsRef={compactActionsRef}
        dateRange={dateRange}
        settings={chartSettings}
        onSettingsChange={handleSettingsChange}
      />
    </div>
  );
};
